import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { green, grey } from "@mui/material/colors";
import { GlobalState } from "context/GlobalContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Divider, IconButton, Switch } from "@mui/material";
import PublishModal from "./PublishModal/PublishModal";
import { publishTableColumn, attributeCollectionTypes, selectedShootPagepath } from "components/PimDashboard/data";
import { converAttrToDatagridCol, createdByColumns, imageTableColumn, publishColumns, styleImageColumn } from "data/globalData";
import { FileDownloadRounded, FilterAltRounded, PlaylistAddRounded } from "@mui/icons-material";
import FileSaver from "file-saver";
import FilterModal from "./FilterModal/FilterModal";
import AddColumnModal from "./AddColumnModal/AddColumnModal";
import { allowAccess } from "utils/userAccessFun";
import { custom, departments } from "data/authData";
import { AuthState } from "context/AuthContext";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import { getShootsFilterColumns, lsModalProfileFilterValStr } from "./data";
import { shootUrlKey } from "../CreateShoot/data";
import { studioAttrGroupLinkedTables } from "../StudioAttributeGroups/data";
import { StudioDashboardState } from "context/StudioDashboardContext";
import moment from "moment";
import IconOnlyCalendarWithCountBadge from "components/PimDashboard/CommonComponents/DateBadgeCalendar/DateBadgeCalendar";
import dayjs from "dayjs";
import TanstackDatagrid from "components/ReusableComponents/TanstackDatagrid/TanstackDatagrid";

const pageFilterLgKey = "shoots_table_attr";

const hasFilterValue = (value) =>
	value !== undefined && value !== null && value !== "" && !(Array.isArray(value) && value.length === 0);

const readStoredFilter = () => {
	try {
		const raw = localStorage.getItem(lsModalProfileFilterValStr);
		const parsed = raw ? JSON.parse(raw) : {};
		if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
		return Object.fromEntries(Object.entries(parsed).filter(([, value]) => hasFilterValue(value)));
	} catch {
		return {};
	}
};

const toTanstackColumn = (col) => {
	if (!col) return null;
	const key = col.key || col.field;
	if (!key) return null;
	const muiValueGetter = col.valueGetter;
	return {
		...col,
		key,
		name: col.name || col.headerName || key,
		align: col.align || "center",
		headerAlign: col.headerAlign || "center",
		minWidth: col.minWidth || 110,
		valueGetter:
			typeof muiValueGetter === "function"
				? ({ value, row }) => {
						try {
							return muiValueGetter(value, row);
						} catch {
							return value ?? "";
						}
					}
				: undefined,
	};
};

const ShootsDashboard = () => {
	const { allAttributes, allAttributesObj } = StudioDashboardState();
	const { user } = AuthState();
	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
	const [tableData, setTableData] = useState([]);
	const [rowCount, setRowCount] = useState(0);
	const [displayImg, setDisplayImg] = useState(true);
	const [selectedRowToPublish, setSelectedRowToPublish] = useState();
	const [tableStatusCount, setTableStatusCount] = useState({
		total: 0,
		pending: 0,
		published: 0,
	});
	const [publishModal, setPublishModal] = useState(false);
	const [filterModal, setFilterModal] = useState(false);
	const [addColumnModal, setAddColumnModal] = useState(false);
	const [highlightedData, setHighlightedData] = useState({});
	const [open, setOpen] = useState(false);
	const iconRef = useRef(null);

	const [attrColumns, setAttrColumns] = useState(() => {
		if (localStorage.getItem(pageFilterLgKey)) {
			return JSON.parse(localStorage.getItem(pageFilterLgKey))
				?.map((row) => {
					return { ...allAttributesObj?.[row] };
				})
				.map((row) => converAttrToDatagridCol(row));
		}
		return allAttributes
			?.filter((row) => row?.linked_table === studioAttrGroupLinkedTables.shoot.key)
			?.map((row) => converAttrToDatagridCol(row));
	});

	const filterColumnOptions = useMemo(() => getShootsFilterColumns(allAttributes), [allAttributes]);
	const [filterColumnVal, setFilterColumnVal] = useState(readStoredFilter);
	const [filterColumns, setFilterColumns] = useState([]);

	useEffect(() => {
		if (!filterColumnOptions.length) return;
		setFilterColumns(filterColumnOptions.filter((row) => hasFilterValue(filterColumnVal?.[row.key])));
	}, [filterColumnOptions, filterColumnVal]);

	const extraColumnsRef = useRef({});
	extraColumnsRef.current = (attrColumns || []).reduce((acc, row) => {
		const key = row?.field || row?.key;
		if (key) acc[key] = true;
		return acc;
	}, {});

	const attrColumnKey = (attrColumns || []).map((row) => row?.field || row?.key).filter(Boolean).join("|");

	const handleCloseFilterModal = () => {
		setFilterModal(false);
	};

	const handleCloseAddColumnModal = () => {
		setAddColumnModal(false);
	};

	const handleFilterClick = () => {
		setFilterModal(true);
	};

	const handleAddColumnClick = () => {
		setAddColumnModal(true);
	};

	const fetchData = useCallback(
		async (limit, offset, filter) => {
			try {
				const { data } = await axios.post(`/api/studio/shoots/${offset}`, {
					filter: filter ?? {},
					extraColumns: extraColumnsRef.current,
					limit,
				});
				const count = data?.result?.count?.[0] ?? {};
				const rows = data?.result?.rows ?? [];
				const hasFilter = filter && Object.keys(filter).length > 0;
				setTableStatusCount({
					total: count.total ?? 0,
					pending: count.pending ?? 0,
					published: count.published ?? 0,
				});
				// A non-empty filter makes the shoots API return the full match set in one response.
				return {
					rows,
					total_rows_count: hasFilter ? rows.length : Number(count.total) || rows.length,
				};
			} catch (error) {
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					message: parseErrorMessage(error),
					severity: "error",
					anchorOrigin: { horizontal: "center", vertical: "top" },
				});
				throw error;
			}
		},
		[setSnackBar, setSnackBarOpen, snackBar],
	);

	const fetchShootsDateData = async (date = dayjs().format("YYYY-MM-DD")) => {
		const query = {
			date,
		};
		try {
			const { data } = await axios.get(`/api/studio/shoots_dates`, {
				params: query,
			});
			const dates = {
				...data.result.reduce((acc, row) => {
					acc[row.date] = row.count;
					return acc;
				}, {}),
			};
			setHighlightedData(dates);
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: parseErrorMessage(error) });
			return null;
		}
	};

	const navigate = useNavigate();

	const handleImageSwitchChange = (checked) => {
		setDisplayImg(checked);
	};

	const handleStyleSelect = async (data) => {
		navigate(`/${selectedShootPagepath}?${shootUrlKey}=${data.shoot_id}`);
	};

	const handleDownloadClick = async () => {
		setLoadingOpen(true);
		try {
			await axios({
				url: `/api/studio/download_modal_profiles`,
				method: "POST",
				responseType: "blob",
				data: { filter: filterColumnVal },
			}).then((res) => {
				const url = URL.createObjectURL(res.data);
				FileSaver.saveAs(url, `allStyles.xlsx`);
				setLoadingOpen(false);
			});
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: parseErrorMessage(error) });
			setLoadingOpen(false);
		}
	};

	const handlePublishClick = useCallback((e, selectedRow) => {
		e.stopPropagation();
		setSelectedRowToPublish(selectedRow);
		setPublishModal(true);
	}, []);

	const onPublishFinalSubmit = async (selectedRow) => {
		setLoadingOpen(true);

		try {
			const res = await axios.post(`/api/studio/publish_shoot`, {
				shoot_id: selectedRow.shoot_id,
				published_date: moment().format("YYYY-MM-DD"),
			});
			setTableData((prev) =>
				prev.map((tableRow) => {
					if (tableRow.shoot_id === selectedRow.shoot_id) {
						return { ...tableRow, ...res.data.result };
					}
					return tableRow;
				}),
			);
			setSelectedRowToPublish();
			setTableStatusCount((prev) => ({ ...prev, published: +prev.published + 1, pending: prev.pending - 1 }));
			setPublishModal(false);
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Shoot Published Successfully",
				severity: "success",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			setLoadingOpen(false);
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: parseErrorMessage(error),
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			setLoadingOpen(false);
		}
	};

	const tableColumns = useMemo(() => {
		const publishColumn = toTanstackColumn(
			publishTableColumn(handlePublishClick, !allowAccess(custom.SHOOT_PUBLISHER.key, user, departments.STUDIO.key)),
		);
		const imageColumn = displayImg
			? {
					...toTanstackColumn(imageTableColumn(styleImageColumn)),
					minWidth: 96,
					maxWidth: 96,
					width: 96,
					flex: 0,
				}
			: null;

		return [
			publishColumn,
			imageColumn,
			...(attrColumns || []).map((col) => toTanstackColumn(col)),
			...createdByColumns.map((col) => ({
				...col,
				align: "center",
				headerAlign: "center",
			})),
		].filter(Boolean);
	}, [attrColumns, displayImg, handlePublishClick, user]);

	return (
		<Box sx={{ height: "calc(100vh - 135px)", p: 1, boxSizing: "border-box", display: "flex", flexDirection: "column", gap: 1 }}>
			<Box display="flex" columnGap={1} pt={0.5} flexWrap="wrap" alignItems="center" rowGap="5px">
				<Button
					variant="contained"
					size="small"
					startIcon={<FilterAltRounded />}
					onClick={handleFilterClick}
					sx={{ textTransform: "capitalize" }}>
					Filter
				</Button>
				:
				{!filterColumns?.length && (
					<Typography variant="body1" fontSize={16} color={grey[700]}>
						None
					</Typography>
				)}
				{filterColumns?.map((row) => (
					<Chip
						key={row.key}
						label={`${row.name} : ${
							Array.isArray(filterColumnVal[row.key]) ? filterColumnVal[row.key]?.join(", ") : filterColumnVal?.[row.key]
						}`}
						size="small"
					/>
				))}
			</Box>

			<Box sx={{ flex: 1, minHeight: 0 }}>
				<TanstackDatagrid
					key={`shoots-grid-${attrColumnKey}`}
					columns={tableColumns}
					pageData={tableData}
					setPageData={setTableData}
					rowCount={rowCount}
					setRowCount={setRowCount}
					rowHeight={displayImg ? 72 : 44}
					filterObj={filterColumnVal}
					setFilterObj={setFilterColumnVal}
					disableColumnFilter
					disableColumnSort
					isLoading={false}
					disablePagination
					emptyMessage="No matching records"
					handleRowClick={({ row }) => handleStyleSelect(row)}
					getServerSideData={fetchData}
					getRowId={(row) => row?.shoot_id}>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1,
							py: 0.5,
							width: "100%",
							minHeight: 44,
						}}>
						<Box display="flex" alignItems="center" flexShrink={0}>
							<Typography variant="h6">Images</Typography>
							<Switch checked={displayImg} onChange={(e) => handleImageSwitchChange(e.target.checked)} />
						</Box>
						<Divider orientation="vertical" flexItem sx={{ height: 36, alignSelf: "center" }} />
						<Box display="flex" alignItems="center" columnGap={1} flexShrink={0}>
							<Typography variant="body1">
								Total: <span style={{ color: grey[700] }}>{tableStatusCount.total}</span>
							</Typography>
							<Divider orientation="vertical" flexItem />
							<Typography variant="body1">
								Published: <span style={{ color: green[600] }}>{tableStatusCount.published}</span>
							</Typography>
							<Divider orientation="vertical" flexItem />
							<Typography variant="body1" fontWeight={600}>
								Pending: <span style={{ color: "red" }}>{tableStatusCount.pending}</span>
							</Typography>
						</Box>
						<Box sx={{ flex: 1 }} />
						<Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
							<Button
								variant="contained"
								sx={{ textTransform: "capitalize" }}
								size="small"
								onClick={handleAddColumnClick}
								startIcon={<PlaylistAddRounded />}>
								Manage Columns
							</Button>
							<Button
								variant="contained"
								size="small"
								sx={{ textTransform: "capitalize" }}
								onClick={handleDownloadClick}
								startIcon={<FileDownloadRounded />}>
								Download
							</Button>
							<IconButton ref={iconRef} onClick={() => setOpen(true)}>
								<CalendarMonthIcon />
							</IconButton>
						</Stack>
					</Box>
				</TanstackDatagrid>
			</Box>

			{open && (
				<IconOnlyCalendarWithCountBadge
					value={dayjs()}
					highlightedData={highlightedData}
					apiCall={(data) => fetchShootsDateData(data)}
					open={open}
					setOpen={() => setOpen(!open)}
					iconRef={iconRef}
				/>
			)}

			<PublishModal
				modal={publishModal}
				setModal={setPublishModal}
				selectedRow={selectedRowToPublish}
				setSelectedRow={setSelectedRowToPublish}
				submitFunction={onPublishFinalSubmit}
			/>
			{filterModal && (
				<FilterModal
					modal={filterModal}
					handleCloseModal={handleCloseFilterModal}
					filterColumns={filterColumns}
					setFilterColumns={setFilterColumns}
					filterColumnVal={filterColumnVal}
					setFilterColumnVal={setFilterColumnVal}
					localStorageStr={lsModalProfileFilterValStr}
					allColumns={filterColumnOptions}
				/>
			)}
			<AddColumnModal
				modal={addColumnModal}
				handleCloseModal={handleCloseAddColumnModal}
				attrColumns={attrColumns}
				setAttrColumns={setAttrColumns}
				localStorageStr={pageFilterLgKey}
				collection={attributeCollectionTypes.SHOOTS}
			/>
		</Box>
	);
};

export default ShootsDashboard;
